// Section tabs that double as a summary: each tab shows its headline number,
// so Map, Counter and Charts read as one view of the day.
import { useEffect } from 'react';
import moment from 'moment';
import Icon from '../../../components/atoms/Icon';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '--');

const SECTIONS = [
  { label: 'Map', key: 'MAP', icon: 'geo-alt-fill' },
  { label: 'Counter', key: 'COUNTER', icon: 'person-walking' },
  { label: 'Charts', key: 'CHARTS', icon: 'bar-chart-fill' },
];

export default function RetailSectionTabs({
  currentSection,
  setCurrentSection,
  currentLocation,
  dayType,
  isToday,
  data,
  currentTime,
  peak,
  avg,
  counterDataAvailable,
  pregameCount,
  gatesOpenCount,
  firstPitchCount,
  regularDayCount,
  queueTimeData,
  counterSensorInfoData,
}) {
  const sections = SECTIONS.filter((s) => currentLocation.sectionTypes?.[s.key]);

  // Same default as before: first available section
  useEffect(() => {
    if (!currentSection && sections.length > 0) setCurrentSection(sections[0].label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isGame = dayType === 'game';
  const max = currentLocation.maxOccupancy || 0;

  // Map: who's in the store at the scrubber time (live), or the day's peak
  const point = data?.[currentTime];
  const peakPoint = isGame ? (peak?.midGame || peak?.preGame) : peak?.wholeDay;
  const mapPreview = isToday && point
    ? { value: fmt(point.total), unit: 'in store', sub: max ? `${Math.round((point.total / max) * 100)}% full` : null }
    : peakPoint
      ? { value: fmt(peakPoint.total), unit: 'peak', sub: peakPoint.time2 ? `at ${moment(peakPoint.time2).format('h:mm A')}` : null }
      : null;

  // Counter: total entries for the day (same totals the Counter tab uses)
  const entries = isGame
    ? (pregameCount || 0) + (gatesOpenCount || 0) + (firstPitchCount || 0)
    : (regularDayCount || 0);
  // Conversion (stores with sales sensors): same math as the Counter tab's Conversion panel
  const salesIds = currentLocation.doorCounterSensorSalesIds || [];
  let conversion = null;
  if (counterDataAvailable && salesIds.length > 0 && entries > 0) {
    const purchases = Object.entries(counterSensorInfoData || {})
      .filter(([id]) => salesIds.includes(parseInt(id, 10)))
      .reduce((sum, [, d]) => sum + (d?.countOut || 0), 0);
    conversion = ((purchases / entries) * 100).toFixed(1);
  }
  const counterSub = isGame ? `${fmt((gatesOpenCount || 0) + (firstPitchCount || 0))} since gates` : (isToday ? 'so far today' : 'for the day');
  const counterPreview = counterDataAvailable
    ? { value: fmt(entries), unit: 'entries', second: conversion != null ? { value: `${conversion}%`, unit: 'conversion' } : null, sub: counterSub }
    : { value: '--', unit: '', sub: 'No entrance counts' };

  // Charts: average occupancy, plus average queue when the store has queue sensors
  const avgMain = isGame ? (avg?.startGame || avg?.preGame) : avg?.wholeDay;
  let queueAvg = null;
  if (queueTimeData && (currentLocation.queueTimeSensorIds || []).length > 0) {
    const values = Object.values(queueTimeData).flat().map((d) => d?.queueTime).filter((v) => typeof v === 'number');
    if (values.length) queueAvg = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  }
  const chartsPreview = max && avgMain != null
    ? { value: fmt(avgMain), unit: 'avg', sub: queueAvg != null ? `${Math.round((avgMain / max) * 100)}% full · ${queueAvg} min avg queue` : `${Math.round((avgMain / max) * 100)}% full on average` }
    : queueAvg != null
      ? { value: fmt(queueAvg), unit: 'min', sub: 'average queue' }
      : { value: null, unit: '', sub: 'Trends for the day' };

  const previews = { Map: mapPreview, Counter: counterPreview, Charts: chartsPreview };

  return (
    <div className="rx-tabs" role="tablist">
      {sections.map((s) => {
        const p = previews[s.label];
        const active = currentSection === s.label;
        return (
          <button
            key={s.label}
            type="button"
            role="tab"
            aria-selected={active}
            className={`rx-tab${active ? ' active' : ''}`}
            onClick={() => setCurrentSection(s.label)}
          >
            <span className="rx-tab-head">
              <Icon name={s.icon} />
              {s.label}
            </span>
            {p?.value != null && (p.second ? (
              // Two numbers: the divider sits under the centre of the label
              <span className="rx-tab-value rx-tab-split">
                <span className="rx-tab-split-left">
                  {p.value}
                  {p.unit && <small>{p.unit}</small>}
                </span>
                <span className="rx-tab-split-divider" aria-hidden="true" />
                <span className="rx-tab-split-right">
                  {p.second.value}
                  <small>{p.second.unit}</small>
                </span>
              </span>
            ) : (
              <span className="rx-tab-value">
                {p.value}
                {p.unit && <small>{p.unit}</small>}
              </span>
            ))}
            {p?.sub && <span className="rx-tab-sub">{p.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}
